import { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import ManageAmenitiesServices from './Role-based/ManageAmenitiesServices';
import { FaSearch, FaTimes, FaEdit, FaTrash } from 'react-icons/fa';
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

function AmenityCategory() {
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    propertyid: 1,
    isactive: true,
    isrelatedtowellness: false,
    file: null,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = () => {
    fetch(`${BQ_BASE_URL}/bq/api/amenitycategories/`)
      .then((response) => response.json())
      .then((data) => setCategories(data))
      .catch((error) => toast.error('Failed to fetch categories'));
  };

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewCategory({
      ...newCategory,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentCategory({
      ...currentCategory,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    setNewCategory({
      ...newCategory,
      file: e.target.files[0],
    });
  };

  const handleEditFileChange = (e) => {
    setCurrentCategory({
      ...currentCategory,
      file: e.target.files[0],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData();
    if (newCategory.file) {
      formData.append('file', newCategory.file);
    }

    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/amenitycategories/?name=${encodeURIComponent(
          newCategory.name
        )}&propertyid=${
          newCategory.propertyid
        }&description=${encodeURIComponent(newCategory.description)}&isactive=${
          newCategory.isactive
        }&isrelatedtowellness=${newCategory.isrelatedtowellness}`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create category');
      }

      const data = await response.json();
      toast.success('Category created successfully!');
      setShowModal(false);
      fetchCategories();
      setNewCategory({
        name: '',
        description: '',
        propertyid: 1,
        isactive: true,
        isrelatedtowellness: false,
        file: null,
      });
    } catch (error) {
      toast.error(error.message || 'Failed to create category');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Define query params early so they're always available
    const queryParams = new URLSearchParams({
      name: currentCategory.name,
      propertyid: currentCategory.propertyid.toString(),
      description: currentCategory.description || '',
      isactive: currentCategory.isactive.toString(),
      isrelatedtowellness: currentCategory.isrelatedtowellness.toString(),
    }).toString();

    let options = {
      method: 'PUT',
    };

    let url = `${BQ_BASE_URL}/bq/api/amenitycategories/${currentCategory.id}`;

    if (currentCategory.file) {
      // Build FormData with ALL fields
      const formData = new FormData();
      formData.append('file', currentCategory.file);
      formData.append('name', currentCategory.name);
      formData.append('propertyid', currentCategory.propertyid.toString());
      formData.append('description', currentCategory.description || '');
      formData.append('isactive', currentCategory.isactive.toString());
      formData.append('isrelatedtowellness', currentCategory.isrelatedtowellness.toString());
      options.body = formData;
    } else {
      // Append query params to URL
      url += `?${queryParams}`;
    }

    try {
      const response = await fetch(url, options);

      if (!response.ok) {
        throw new Error('Failed to update category');
      }

      const data = await response.json();
      toast.success('Category updated successfully!');
      setShowEditModal(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.message || 'Failed to update category');
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = (category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!categoryToDelete) return;

    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/amenitycategories/${categoryToDelete.id}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete category');
      }

      const result = await response.json();
      toast.success(result.message || 'Category deleted successfully!');
      fetchCategories();
    } catch (error) {
      toast.error(error.message || 'Failed to delete category');
    } finally {
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    }
  };

  const openEditModal = (category) => {
    setCurrentCategory({
      ...category,
      file: null,
    });
    setShowEditModal(true);
  };

  return (
    <div className="mx-auto p-6">
      <ManageAmenitiesServices />
      <ToastContainer position="top-right" autoClose={3000} />

      <h1 className="text-2xl font-bold mt-12 mb-6">Amenities Categories</h1>

      <div className="flex items-center gap-4 mb-8">
        <div className="relative flex-grow max-w-md">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search Categories..."
            className="border rounded-lg py-2 pl-10 pr-4 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-black hover:text-white text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2"
        >
          <span>+</span> Add Category
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCategories.map((category) => (
          <div
            key={category.id}
            className="border rounded-lg overflow-hidden shadow-lg bg-white relative"
          >
            <div className="relative">
              <img
                src={category.imageurl}
                alt={category.name}
                className="w-full h-58 object-cover"
              />
              <div className="absolute top-2 right-2 flex gap-2">
                <button
                  onClick={() => openEditModal(category)}
                  className="bg-white p-2 rounded-full hover:bg-gray-200"
                >
                  <FaEdit className="text-blue-500" />
                </button>
                <button
                  onClick={() => confirmDelete(category)}
                  className="bg-white p-2 rounded-full hover:bg-gray-200"
                >
                  <FaTrash className="text-red-500" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">{category.name}</h2>
                <span
                  className={`px-3 py-1 text-sm font-medium rounded-full ${
                    category.isactive ? 'bg-green-200 text-black' : 'bg-red-500 text-white'
                  }`}
                >
                  {category.isactive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-gray-600 mt-2">{category.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Add Category Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-[500px]">
            <div className="flex justify-between items-center border-b p-4">
              <h2 className="text-xl font-semibold">Add New Category</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4">
              <div className="mb-2">
                <label className="block text-gray-700 mb-2 text-base">Category Name</label>
                <input
                  type="text"
                  name="name"
                  value={newCategory.name}
                  onChange={handleInputChange}
                  placeholder="Enter amenity category name."
                  className="w-full border rounded-lg p-2 text-sm"
                  required
                />
              </div>

              <div className="mb-2">
                <label className="block text-gray-700 mb-2 text-base">Description</label>
                <textarea
                  name="description"
                  value={newCategory.description}
                  onChange={handleInputChange}
                  placeholder="Enter amenity description here."
                  className="w-full border rounded-lg p-2 text-sm"
                  rows="2"
                  required
                />
              </div>

              {/* <div className="mb-4">
                <label className="block text-gray-700 mb-2">Property ID</label>
                <input
                  type="number"
                  name="propertyid"
                  value={newCategory.propertyid}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg p-2"
                  required
                />
              </div> */}

              <div className="mb-2">
                <label className="block text-gray-700 mb-2">Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full border rounded-lg p-2 text-sm"
                  required
                />
              </div>

              <div className="grid grid-cols-1 gap-4 mb-2">
                {/* Active Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <label
                      htmlFor="isactive"
                      className="block text-sm font-bold text-gray-700 mb-1"
                    >
                      Customer Visibilty
                    </label>
                    <p className="text-sm text-gray-500">
                      Toggle ON to make this category visible to customers.
                    </p>
                  </div>
                  <label
                    htmlFor="isactive"
                    className="relative inline-flex items-center cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      id="isactive"
                      name="isactive"
                      checked={newCategory.isactive}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div
                      className={`w-12 h-6 rounded-full transition-colors ${
                        newCategory.isactive ? 'bg-black' : 'bg-gray-300'
                      }`}
                    ></div>
                    <div
                      className={`dot absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        newCategory.isactive ? 'translate-x-6' : ''
                      }`}
                    ></div>
                  </label>
                </div>

                {/* Related to Wellness Toggle */}
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <label
                      htmlFor="isrelatedtowellness"
                      className="block text-sm font-bold text-gray-700 mb-1"
                    >
                      Payment option
                    </label>
                    <p className="text-sm text-gray-500">
                      Toggle ON to hide payment options guests see only Pay Later or Pay at Front
                      Desk.
                    </p>
                  </div>
                  <label
                    htmlFor="isrelatedtowellness"
                    className="relative inline-flex items-center cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      id="isrelatedtowellness"
                      name="isrelatedtowellness"
                      checked={newCategory.isrelatedtowellness}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div
                      className={`w-12 h-6 rounded-full transition-colors ${
                        newCategory.isrelatedtowellness ? 'bg-black' : 'bg-gray-300'
                      }`}
                    ></div>
                    <div
                      className={`dot absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        newCategory.isrelatedtowellness ? 'translate-x-6' : ''
                      }`}
                    ></div>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-lg text-sm bg-red-500 text-white hover:text-black"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-black text-sm text-white hover:text-white rounded-lg disabled:opacity-50"
                >
                  {isLoading ? 'Creating...' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditModal && currentCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-[500px]">
            <div className="flex justify-between items-center  px-4 py-2">
              <h2 className="text-xl font-semibold">Edit Category</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes size={24} />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="px-4 py-2">
              <div className="grid grid-cols-1">
                {/* Left Column */}
                <div className="space-y-2">
                  <div>
                    <label className="block text-gray-700 mb-1 text-sm">Category Name</label>
                    <input
                      type="text"
                      name="name"
                      value={currentCategory.name}
                      onChange={handleEditInputChange}
                      className="w-full border rounded-lg p-2 text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-1 text-sm">Description</label>
                    <textarea
                      name="description"
                      value={currentCategory.description}
                      onChange={handleEditInputChange}
                      className="w-full border rounded-lg p-2 text-sm"
                      rows="3"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-1 text-sm">Property ID</label>
                    <input
                      type="number"
                      name="propertyid"
                      value={currentCategory.propertyid}
                      onChange={handleEditInputChange}
                      className="w-full border rounded-lg p-2 text-sm"
                      required
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-2">
                  {currentCategory.imageurl && (
                    <div>
                      <label className="block text-gray-700 mb-2 text-sm">Current Image</label>
                      <img
                        src={currentCategory.imageurl}
                        alt={currentCategory.name}
                        className="w-20 h-15 object-cover border rounded-lg"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-gray-700 mb-2 text-sm">
                      {currentCategory.imageurl ? 'Change Image' : 'Upload Image'}
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleEditFileChange}
                      className="w-full border rounded-lg p-2 text-xs"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-2 mt-3">
                    {/* Active and Related to Wellness Toggles */}
                    <div className="flex flex-col gap-2">
                      {/* Active Toggle */}
                      <div className="flex items-center justify-between">
                        <div>
                          <label
                            htmlFor="isactive"
                            className="block text-sm font-bold text-gray-700 mb-1"
                          >
                            Active
                          </label>
                          <p className="text-sm text-gray-500">
                            Toggle ON to make this category visible to customers.
                          </p>
                        </div>
                        <label
                          htmlFor="isactive"
                          className="relative inline-flex items-center cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            id="isactive"
                            name="isactive"
                            checked={currentCategory.isactive}
                            onChange={handleEditInputChange}
                            className="sr-only"
                          />
                          <div
                            className={`w-12 h-6 rounded-full transition-colors ${
                              currentCategory.isactive ? 'bg-black' : 'bg-gray-300'
                            }`}
                          ></div>
                          <div
                            className={`dot absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                              currentCategory.isactive ? 'translate-x-6' : ''
                            }`}
                          ></div>
                        </label>
                      </div>

                      {/* Related to Wellness Toggle */}
                      <div className="flex items-center justify-between">
                        <div>
                          <label
                            htmlFor="isrelatedtowellness"
                            className="block text-sm font-bold text-gray-700 mb-1"
                          >
                            Payment Option
                          </label>
                          <p className="text-sm text-gray-500">
                            Toggle ON to include payment options.
                          </p>
                        </div>
                        <label
                          htmlFor="isrelatedtowellness"
                          className="relative inline-flex items-center cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            id="isrelatedtowellness"
                            name="isrelatedtowellness"
                            checked={currentCategory.isrelatedtowellness}
                            onChange={handleEditInputChange}
                            className="sr-only"
                          />
                          <div
                            className={`w-12 h-6 rounded-full transition-colors ${
                              currentCategory.isrelatedtowellness ? 'bg-black' : 'bg-gray-300'
                            }`}
                          ></div>
                          <div
                            className={`dot absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                              currentCategory.isrelatedtowellness ? 'translate-x-6' : ''
                            }`}
                          ></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer buttons */}
              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-red-500 text-sm text-white hover:text-black border rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-black text-sm hover:text-white text-white rounded-lg disabled:opacity-50"
                >
                  {isLoading ? 'Updating...' : 'Update Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && categoryToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-[500px]">
            <div className="flex justify-between items-center border-b p-4">
              <h2 className="text-xl font-semibold">Confirm Deletion</h2>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-6">
              <p className="text-lg mb-6">
                Are you sure you want to delete the category{' '}
                <span className="font-semibold">"{categoryToDelete.name}"</span>? This action cannot
                be undone.
              </p>

              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 border bg-black text-white rounded-lg hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isLoading}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:text-black disabled:opacity-50"
                >
                  {isLoading ? 'Deleting...' : 'Delete Category'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AmenityCategory;
